/**
 * AI Tools for Calendar Events
 *
 * Tools that allow Marta to interact with calendar events via GraphQL.
 */

import type { ToolDefinition } from './types';
import { graphqlQuery, graphqlMutation } from '../../database/graphqlClient';

// =============================================================================
// GRAPHQL QUERIES & MUTATIONS
// =============================================================================

const EVENT_FIELDS = `
  id
  title
  description
  startDate
  endDate
  allDay
  source
  category
  color
  location
  attendees
  taskId
  projectId
  milestoneId
  dealId
  invoiceId
  contactId
  activityId
  reminderMinutes
  isRecurring
  recurrenceRule
  createdAt
  updatedAt
`;

const QUERIES = {
  events: `query { calendarEvents { ${EVENT_FIELDS} } }`,
};

const MUTATIONS = {
  createEvent: `mutation CreateCalendarEvent($input: CalendarEventInput!) { createCalendarEvent(input: $input) { ${EVENT_FIELDS} } }`,
  deleteEvent: `mutation DeleteCalendarEvent($id: ID!) { deleteCalendarEvent(id: $id) }`,
};

// =============================================================================
// TOOL DEFINITIONS
// =============================================================================

export const listEventsTool: ToolDefinition = {
  name: 'list_events',
  description: 'Elenca gli eventi del calendario. Può filtrare per data, categoria o progetto.',
  category: 'calendar',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.events,
  parameters: {
    type: 'object',
    properties: {
      startDate: {
        type: 'string',
        description: 'Data di inizio per filtrare (formato YYYY-MM-DD)',
      },
      endDate: {
        type: 'string',
        description: 'Data di fine per filtrare (formato YYYY-MM-DD)',
      },
      category: {
        type: 'string',
        description: 'Categoria evento',
        enum: ['meeting', 'deadline', 'reminder', 'call', 'event', 'task', 'milestone', 'other'],
      },
      projectId: {
        type: 'string',
        description: 'ID del progetto per filtrare',
      },
      limit: {
        type: 'number',
        description: 'Numero massimo di risultati (default 20)',
      },
    },
  },
};

export const searchEventsTool: ToolDefinition = {
  name: 'search_events',
  description: 'Cerca eventi per titolo o descrizione.',
  category: 'calendar',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.events,
  parameters: {
    type: 'object',
    properties: {
      searchTerm: {
        type: 'string',
        description: 'Termine di ricerca',
      },
      limit: {
        type: 'number',
        description: 'Numero massimo di risultati (default 10)',
      },
    },
    required: ['searchTerm'],
  },
};

export const createEventTool: ToolDefinition = {
  name: 'create_event',
  description: 'Crea un nuovo evento nel calendario.',
  category: 'calendar',
  operation: 'create',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.createEvent,
  parameters: {
    type: 'object',
    properties: {
      title: {
        type: 'string',
        description: 'Titolo dell\'evento',
      },
      description: {
        type: 'string',
        description: 'Descrizione dell\'evento',
      },
      startDate: {
        type: 'string',
        description: 'Data e ora di inizio (formato ISO: YYYY-MM-DDTHH:mm:ss)',
      },
      endDate: {
        type: 'string',
        description: 'Data e ora di fine (formato ISO: YYYY-MM-DDTHH:mm:ss)',
      },
      allDay: {
        type: 'boolean',
        description: 'Se true, l\'evento dura tutto il giorno',
        default: false,
      },
      category: {
        type: 'string',
        description: 'Categoria dell\'evento',
        enum: ['meeting', 'deadline', 'reminder', 'call', 'event', 'task', 'milestone', 'other'],
        default: 'event',
      },
      location: {
        type: 'string',
        description: 'Luogo dell\'evento',
      },
      color: {
        type: 'string',
        description: 'Colore dell\'evento (es. #3b82f6)',
      },
      attendees: {
        type: 'array',
        items: { type: 'string' },
        description: 'Lista dei partecipanti',
      },
      reminderMinutes: {
        type: 'number',
        description: 'Minuti prima dell\'evento per il promemoria',
      },
      projectId: {
        type: 'string',
        description: 'ID del progetto associato',
      },
      contactId: {
        type: 'string',
        description: 'ID del contatto associato',
      },
    },
    required: ['title', 'startDate', 'endDate'],
  },
};

export const createReminderTool: ToolDefinition = {
  name: 'create_reminder',
  description: 'Crea un promemoria (evento di tipo reminder).',
  category: 'calendar',
  operation: 'create',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.createEvent,
  parameters: {
    type: 'object',
    properties: {
      title: {
        type: 'string',
        description: 'Testo del promemoria',
      },
      dateTime: {
        type: 'string',
        description: 'Data e ora del promemoria (formato ISO: YYYY-MM-DDTHH:mm:ss)',
      },
      reminderMinutes: {
        type: 'number',
        description: 'Minuti prima per la notifica (default 15)',
        default: 15,
      },
    },
    required: ['title', 'dateTime'],
  },
};

export const deleteEventTool: ToolDefinition = {
  name: 'delete_event',
  description: 'Elimina un evento dal calendario. ATTENZIONE: Questa azione è irreversibile.',
  category: 'calendar',
  operation: 'delete',
  requiresConfirmation: true,
  graphqlOperation: MUTATIONS.deleteEvent,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID dell\'evento da eliminare',
      },
      eventTitle: {
        type: 'string',
        description: 'Titolo dell\'evento (per conferma)',
      },
    },
    required: ['id'],
  },
};

export const getTodaysEventsTool: ToolDefinition = {
  name: 'get_todays_events',
  description: 'Ottiene tutti gli eventi di oggi.',
  category: 'calendar',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.events,
  parameters: {
    type: 'object',
    properties: {},
  },
};

export const getUpcomingEventsTool: ToolDefinition = {
  name: 'get_upcoming_events',
  description: 'Ottiene gli eventi imminenti (prossimi 7 giorni).',
  category: 'calendar',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.events,
  parameters: {
    type: 'object',
    properties: {
      days: {
        type: 'number',
        description: 'Numero di giorni da considerare (default 7)',
        default: 7,
      },
    },
  },
};

// =============================================================================
// TOOL EXECUTION FUNCTIONS
// =============================================================================

interface CalendarEvent {
  id: string;
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  allDay?: boolean;
  category?: string;
  projectId?: string;
  [key: string]: unknown;
}

export async function executeListEvents(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ calendarEvents: CalendarEvent[] }>(QUERIES.events);
  let events = result.calendarEvents || [];

  // Apply date filters
  if (params.startDate) {
    const startDate = new Date(String(params.startDate));
    events = events.filter(e => new Date(e.startDate || '') >= startDate);
  }
  if (params.endDate) {
    const endDate = new Date(String(params.endDate));
    events = events.filter(e => new Date(e.startDate || '') <= endDate);
  }

  // Apply category filter
  if (params.category) {
    events = events.filter(e => e.category === params.category);
  }

  // Apply project filter
  if (params.projectId) {
    events = events.filter(e => e.projectId === params.projectId);
  }

  // Sort by startDate
  events.sort((a, b) => new Date(a.startDate || '').getTime() - new Date(b.startDate || '').getTime());

  // Apply limit
  const limit = typeof params.limit === 'number' ? params.limit : 20;
  events = events.slice(0, limit);

  return { events, count: events.length };
}

export async function executeSearchEvents(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ calendarEvents: CalendarEvent[] }>(QUERIES.events);
  let events = result.calendarEvents || [];

  const term = String(params.searchTerm || '').toLowerCase();
  events = events.filter(
    e =>
      e.title?.toLowerCase().includes(term) ||
      e.description?.toLowerCase().includes(term)
  );

  // Sort by startDate
  events.sort((a, b) => new Date(a.startDate || '').getTime() - new Date(b.startDate || '').getTime());

  // Apply limit
  const limit = typeof params.limit === 'number' ? params.limit : 10;
  events = events.slice(0, limit);

  return { events, count: events.length };
}

export async function executeCreateEvent(params: Record<string, unknown>): Promise<unknown> {
  const input = {
    title: params.title,
    description: params.description || null,
    startDate: params.startDate,
    endDate: params.endDate,
    allDay: params.allDay || false,
    source: 'manual',
    category: params.category || 'event',
    color: params.color || null,
    location: params.location || null,
    attendees: params.attendees || [],
    taskId: params.taskId || null,
    projectId: params.projectId || null,
    milestoneId: params.milestoneId || null,
    dealId: params.dealId || null,
    invoiceId: params.invoiceId || null,
    contactId: params.contactId || null,
    activityId: params.activityId || null,
    reminderMinutes: params.reminderMinutes || null,
    isRecurring: false,
    recurrenceRule: null,
  };

  const result = await graphqlMutation<{ createCalendarEvent: unknown }>(MUTATIONS.createEvent, { input });
  return result.createCalendarEvent;
}

export async function executeCreateReminder(params: Record<string, unknown>): Promise<unknown> {
  const dateTime = new Date(String(params.dateTime));
  const endDateTime = new Date(dateTime.getTime() + 30 * 60 * 1000); // 30 minutes duration

  const input = {
    title: params.title,
    description: null,
    startDate: dateTime.toISOString(),
    endDate: endDateTime.toISOString(),
    allDay: false,
    source: 'manual',
    category: 'reminder',
    color: '#f59e0b', // Amber for reminders
    location: null,
    attendees: [],
    taskId: null,
    projectId: null,
    milestoneId: null,
    dealId: null,
    invoiceId: null,
    contactId: null,
    activityId: null,
    reminderMinutes: params.reminderMinutes || 15,
    isRecurring: false,
    recurrenceRule: null,
  };

  const result = await graphqlMutation<{ createCalendarEvent: unknown }>(MUTATIONS.createEvent, { input });
  return result.createCalendarEvent;
}

export async function executeDeleteEvent(params: Record<string, unknown>): Promise<unknown> {
  await graphqlMutation(MUTATIONS.deleteEvent, { id: params.id });
  return { deleted: true, id: params.id };
}

export async function executeGetTodaysEvents(): Promise<unknown> {
  const result = await graphqlQuery<{ calendarEvents: CalendarEvent[] }>(QUERIES.events);
  const events = result.calendarEvents || [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const todaysEvents = events.filter(e => {
    const eventDate = new Date(e.startDate || '');
    return eventDate >= today && eventDate < tomorrow;
  });

  todaysEvents.sort((a, b) => new Date(a.startDate || '').getTime() - new Date(b.startDate || '').getTime());

  return { events: todaysEvents, count: todaysEvents.length, date: today.toISOString().split('T')[0] };
}

export async function executeGetUpcomingEvents(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ calendarEvents: CalendarEvent[] }>(QUERIES.events);
  const events = result.calendarEvents || [];

  const days = typeof params.days === 'number' ? params.days : 7;
  const now = new Date();
  const endDate = new Date(now);
  endDate.setDate(endDate.getDate() + days);

  const upcomingEvents = events.filter(e => {
    const eventDate = new Date(e.startDate || '');
    return eventDate >= now && eventDate <= endDate;
  });

  upcomingEvents.sort((a, b) => new Date(a.startDate || '').getTime() - new Date(b.startDate || '').getTime());

  return {
    events: upcomingEvents,
    count: upcomingEvents.length,
    fromDate: now.toISOString().split('T')[0],
    toDate: endDate.toISOString().split('T')[0],
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

export const calendarTools: ToolDefinition[] = [
  listEventsTool,
  searchEventsTool,
  createEventTool,
  createReminderTool,
  deleteEventTool,
  getTodaysEventsTool,
  getUpcomingEventsTool,
];

export const calendarToolExecutors: Record<string, (params: Record<string, unknown>) => Promise<unknown>> = {
  list_events: executeListEvents,
  search_events: executeSearchEvents,
  create_event: executeCreateEvent,
  create_reminder: executeCreateReminder,
  delete_event: executeDeleteEvent,
  get_todays_events: executeGetTodaysEvents,
  get_upcoming_events: executeGetUpcomingEvents,
};
